/**
 * Six slots, always in the same order, one per channel.
 *
 * This is the design's central idea for the publishing table, and the reason is
 * worth keeping next to the code:
 *
 *   "a fixed row of 6 small dots, one per channel in a constant order, filled
 *    with that channel's color when the post includes it and left as a pale
 *    outline otherwise — this keeps every channel in the same horizontal
 *    position on every row (e.g. Facebook's dot always lines up vertically down
 *    the table), so scanning 'which posts touch channel X' is a glance, not a
 *    re-read of text per row."
 *
 * So the empty slots are the feature. Rendering only the active channels would
 * be tidier and would destroy the alignment the column exists for.
 *
 * When a post has exactly one channel its name is printed underneath; with
 * several, the dots and their tooltips carry it, because six names would be
 * wider than the column and slower to read than the colours.
 */
import { CHANNELS, CHANNEL_COLORS, type Channel } from '@/types/posts'
import './ChannelDots.css'

interface Props {
  channels: Channel[]
  /** Kanban cards show only the filled dots — there is no column to align to. */
  filledOnly?: boolean
}

export function ChannelDots({ channels, filledOnly = false }: Props) {
  const on = new Set(channels)
  const shown = filledOnly ? CHANNELS.filter((c) => on.has(c)) : CHANNELS

  return (
    <div className={filledOnly ? 'chdots chdots--tight' : 'chdots'}>
      {shown.map((c) => (
        <span
          key={c}
          className={`chdots__dot${on.has(c) ? '' : ' chdots__dot--off'}`}
          style={on.has(c) ? { background: CHANNEL_COLORS[c], borderColor: CHANNEL_COLORS[c] } : undefined}
          title={c}
        />
      ))}
    </div>
  )
}

/** The single-channel caption under the dots. Empty when there are several. */
export function soloChannelName(channels: Channel[]): string {
  return channels.length === 1 ? channels[0]! : ''
}
